import { Injectable } from '@angular/core';
import { area as turfArea } from '@turf/turf';
import { ExtendedPolygon, ISOXMLManager, Polygon, PolygonPolygonTypeEnum } from 'isoxml';
import { from, Observable } from 'rxjs';
import { Field, Polygon as FieldPolygon } from '../fields/field.model';
import { formatImportError, ImportBase, ParsedImportFile } from './import.service';

@Injectable({ providedIn: 'root' })
export class ImportIsoxml extends ImportBase {
  override loadFields(files: File[]): Observable<ParsedImportFile[]> {
    return from(Promise.all(files.map((file) => this.loadFieldsFromFile(file))));
  }

  private loadFieldsFromFile(file: File): Promise<ParsedImportFile> {
    if (this.isZipFile(file)) {
      return this.loadFieldsFromZip(file);
    }
    return this.loadFieldsFromXML(file);
  }

  private async loadFieldsFromZip(file: File): Promise<ParsedImportFile> {
    const isoxmlManager = new ISOXMLManager();
    try {
      const buff = await file.arrayBuffer();
      const fileArray = new Uint8Array(buff);
      await isoxmlManager.parseISOXMLFile(fileArray, 'application/zip');

      return {
        filename: file.name,
        fields: this.processIsoXML(isoxmlManager),
      };
    } catch (error) {
      return {
        filename: file.name,
        errorMessage: formatImportError(error),
      };
    }
  }

  private async loadFieldsFromXML(file: File): Promise<ParsedImportFile> {
    const isoxmlManager = new ISOXMLManager();
    try {
      const content = await file.text();
      await isoxmlManager.parseISOXMLFile(content, 'text/xml');

      return {
        filename: file.name,
        fields: this.processIsoXML(isoxmlManager),
      };
    } catch (error) {
      return {
        filename: file.name,
        errorMessage: formatImportError(error),
      };
    }
  }

  private processIsoXML(isoxmlManager: ISOXMLManager): Field[] {
    // getWarnings() method returns all the warnings from the last parsing
    // console.log(isoxmlManager.getWarnings())

    const fields: Field[] = [];

    isoxmlManager.rootElement.attributes.Partfield?.forEach((partfield) => {
      const polygons = partfield.attributes.PolygonnonTreatmentZoneonly;
      if (!polygons || polygons.length == 0) {
        return;
      }

      // In isoxml version 4 and later, multiple polygons of type PartfieldBoundary, each representing a bounded area of the Partfield, can be included in a single Partfield.
      // Prior to version 4, only a single Polygon representing a boundary could be included in a Partfield.
      // Currently, in case there are multiple PartfieldBoundary polygons, the first one is used.
      // The current Polygon model in this app does not support multiple boundaries
      // TODO: add support for multiple PartfieldBoundary polygons
      const boundaryPolygons = polygons.filter(poly => poly.attributes.PolygonType === PolygonPolygonTypeEnum.PartfieldBoundary);

      if (boundaryPolygons.length === 0) {
        return;
      }
      if (boundaryPolygons.length > 1) {
        console.warn(
          'Partfield contains multiple polygon components. Only the first component can be mapped to the current field model.',
          partfield.attributes.PartfieldDesignator
        );
      }

      // add boundaryPolygon as first polygon to fieldCoordinates 
      const boundaryPolygon = boundaryPolygons[0];
      const geoJson = ExtendedPolygon.toGeoJSON([boundaryPolygon]);
      if (!geoJson || geoJson.coordinates.length === 0) {
        return;
      }
      const fieldCoordinates = geoJson.coordinates[0];

      // add innerPolygons to fieldCoordinates
      const innerPolygons = polygons.filter(
        (poly) =>
          ![
            PolygonPolygonTypeEnum.PartfieldBoundary,
            PolygonPolygonTypeEnum.Headland
          ].includes( poly.attributes.PolygonType),
      );
      innerPolygons.forEach(innerPolygon => {
        const geoJson = ExtendedPolygon.toGeoJSON([innerPolygon]);
        if (!geoJson || geoJson.coordinates.length === 0) {
            return;
        }
        fieldCoordinates.push(geoJson.coordinates[0][0]);
      })

      const newField = new Field();
      newField.name = partfield.attributes.PartfieldDesignator || 'unknown';
      newField.area = turfArea({
        type: 'Polygon',
        coordinates: fieldCoordinates,
      }) / 10000;
      newField.polygon = new FieldPolygon(fieldCoordinates);
      newField.isSelected = false;
      fields.push(newField);
    });
    console.log('fields included in isoXML file: ' + JSON.stringify(fields));
    return fields;
  }
}
